import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useApiClient } from "@/hooks/use-api-client";
import {
  getAccessToken,
  createAuthHeaders,
  isSessionValid,
} from "@/lib/auth-utils";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, CheckCircle, XCircle, ArrowRight } from "lucide-react";
import { supabase } from "@/lib/supabase";

interface FlowStep {
  id: string;
  name: string;
  description: string;
  status: "pending" | "success" | "error";
  details?: string;
}

interface TestResult {
  method?: string;
  data?: unknown;
  error?: string;
  details?: unknown;
}

export function AuthFlowTest() {
  const { user, signIn, signOut } = useAuth();
  const apiClient = useApiClient();
  const [steps, setSteps] = useState<FlowStep[]>([]);
  const [loading, setLoading] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);

  const initialSteps: FlowStep[] = [
    {
      id: "step1",
      name: "User Authentication",
      description: "User is logged in via Supabase Auth",
      status: "pending",
    },
    {
      id: "step2",
      name: "JWT Token Retrieval",
      description: "JWT token is obtained from Supabase session",
      status: "pending",
    },
    {
      id: "step3",
      name: "Token Validation",
      description: "JWT token is valid and not expired",
      status: "pending",
    },
    {
      id: "step4",
      name: "Authorization Header",
      description: "Bearer token is included in Authorization header",
      status: "pending",
    },
    {
      id: "step5",
      name: "API Request",
      description: "Authenticated request is sent to backend",
      status: "pending",
    },
    {
      id: "step6",
      name: "Backend Response",
      description: "Backend accepts the JWT token and responds",
      status: "pending",
    },
  ];

  useEffect(() => {
    setSteps(initialSteps);
  }, []);

  const updateStep = (
    stepId: string,
    status: "pending" | "success" | "error",
    details?: string
  ) => {
    setSteps((prev) =>
      prev.map((step) =>
        step.id === stepId ? { ...step, status, details } : step
      )
    );
  };

  const testAuthFlow = async () => {
    setLoading(true);
    setSteps(initialSteps);
    setTestResult(null);

    try {

      // Step 1: User Authentication
      if (!user) {
        updateStep("step1", "error", "No user found. Please log in first.");
        return;
      }
      updateStep("step1", "success", `User authenticated: ${user.email}`);

      // Step 2: JWT Token Retrieval
      const token = await getAccessToken();
      if (!token) {
        updateStep("step2", "error", "No JWT token found in session");
        return;
      }
      updateStep(
        "step2",
        "success",
        `Token retrieved (${token.length} characters)`
      );

      // Step 3: Token Validation
      const isValid = await isSessionValid();
      if (!isValid) {
        updateStep("step3", "error", "Token is invalid or expired");
        return;
      }
      updateStep("step3", "success", "Token is valid and not expired");

      // Step 4: Authorization Header
      const authHeaders = await createAuthHeaders();
      if (!authHeaders.Authorization) {
        updateStep("step4", "error", "No Authorization header generated");
        return;
      }
      updateStep(
        "step4",
        "success",
        `Authorization: ${authHeaders.Authorization.substring(0, 30)}...`
      );

      // Step 5: API Request
      updateStep("step5", "pending", "Sending request to backend...");

      // Test with API client
      const response =
        await apiClient.apiClient.draftDocumentEndpointApiV1DraftDocumentPost({
          document_type: "contrato_laboral",
          parties: [
            { name: "Empresa ABC", role: "Empleador" },
            { name: "Juan Pérez", role: "Trabajador" },
          ],
          key_requirements: ["Término indefinido", "Período de prueba"],
        });

      updateStep("step5", "success", "Request sent successfully");

      // Step 6: Backend Response
      if (response && response.data) {
        updateStep(
          "step6",
          "success",
          "Backend accepted JWT token and responded"
        );
        setTestResult({
          method: "API Client",
          data: response.data,
        });
      } else {
        updateStep("step6", "error", "No response data received from backend");
      }
    } catch (error: unknown) {
      console.error("❌ Authentication flow test failed:", error);

      // Update the current step with error
      const currentStep = steps.find((step) => step.status === "pending");
      if (currentStep) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error occurred";
        updateStep(currentStep.id, "error", errorMessage);
      }

      setTestResult({
        error: error instanceof Error ? error.message : "Unknown error",
        details: error,
      });
    } finally {
      setLoading(false);
    }
  };

  const testDirectFetch = async () => {
    if (!user) return;

    setLoading(true);
    setTestResult(null);

    try {

      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error("No access token available");
      }

      const response = await fetch(
        "https://88e61916f97b.ngrok-free.app/api/v1/draft-document",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            document_type: "contrato_laboral",
            parties: [
              { name: "Empresa ABC", role: "Empleador" },
              { name: "Juan Pérez", role: "Trabajador" },
            ],
            key_requirements: ["Término indefinido", "Período de prueba"],
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${JSON.stringify(data)}`);
      }

      setTestResult({
        method: "Direct Fetch",
        data: data,
      });

    } catch (error: unknown) {
      console.error("❌ Direct fetch failed:", error);
      setTestResult({
        method: "Direct Fetch",
        error: error instanceof Error ? error.message : "Unknown error",
        details: error,
      });
    } finally {
      setLoading(false);
    }
  };

  const getStepIcon = (status: string) => {
    switch (status) {
      case "success":
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case "error":
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return (
          <div className="h-5 w-5 rounded-full border-2 border-gray-300" />
        );
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Authentication Flow Test</CardTitle>
          <CardDescription>
            Test the complete authentication flow from Supabase Auth to backend
            API
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Test Buttons */}
          <div className="flex space-x-2">
            <Button
              onClick={testAuthFlow}
              disabled={loading || !user}
              className="flex-1">
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Testing Flow...
                </>
              ) : (
                <>
                  <ArrowRight className="mr-2 h-4 w-4" />
                  Test Complete Flow
                </>
              )}
            </Button>

            <Button
              onClick={testDirectFetch}
              disabled={loading || !user}
              variant="outline"
              className="flex-1">
              Test Direct Fetch
            </Button>
          </div>

          {/* Flow Steps */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium">Authentication Flow Steps:</h4>
            {steps.map((step, index) => (
              <div
                key={step.id}
                className="flex items-start space-x-3 p-3 border rounded-lg">
                <div className="flex items-center space-x-2">
                  {getStepIcon(step.status)}
                  <span className="text-sm font-medium text-gray-500">
                    {index + 1}
                  </span>
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <h5 className="text-sm font-medium">{step.name}</h5>
                    <Badge
                      variant={
                        step.status === "success"
                          ? "default"
                          : step.status === "error"
                          ? "destructive"
                          : "secondary"
                      }>
                      {step.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {step.description}
                  </p>
                  {step.details && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {step.details}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Test Results */}
          {testResult && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Test Results:</h4>
              <Alert variant={testResult.error ? "destructive" : "default"}>
                <AlertDescription>
                  {testResult.error ? (
                    <div>
                      <strong>Error:</strong> {testResult.error}
                      {testResult.details && (
                        <details className="mt-2">
                          <summary className="cursor-pointer text-sm">
                            View Details
                          </summary>
                          <pre className="mt-2 text-xs bg-muted p-2 rounded overflow-auto">
                            {JSON.stringify(testResult.details, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  ) : (
                    <div>
                      <strong>Success!</strong>{" "}
                      {testResult.method || "API Client"} call completed
                      successfully.
                      <details className="mt-2">
                        <summary className="cursor-pointer text-sm">
                          View Response
                        </summary>
                        <pre className="mt-2 text-xs bg-muted p-2 rounded overflow-auto">
                          {JSON.stringify(testResult.data, null, 2)}
                        </pre>
                      </details>
                    </div>
                  )}
                </AlertDescription>
              </Alert>
            </div>
          )}

          {/* User Status */}
          {!user && (
            <Alert variant="destructive">
              <AlertDescription>
                Please log in first to test the authentication flow.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
